import { cloneDeep } from 'lodash';
import React, { useEffect } from 'react';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import {
  IProposalMarineHullDetails,
  IProposalPageFormState,
} from '../../page/generic/form';
import { validateMarineHullDetailsField } from '../../page/generic/validation';
import TextInputFormField from '@components/form-fields/TextInputFormField';

interface IProposalMarineHullDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
}

const ProposalMarineHullDetailsWidget: React.FC<
  IProposalMarineHullDetailsWidgetProps
> = ({ pageState, onPageStateUpdate, disabledForm }) => {
  const values = pageState.values.marineHullDetails;
  const errors = pageState.errors.marineHullDetails;
  const touched = pageState.touched.marineHullDetails;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const initialize = async () => {};

  useEffect(() => {
    initialize();
  }, []);

  const onFieldBlur = async (fieldName: string) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.marineHullDetails[fieldName] = true;
    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IProposalMarineHullDetails,
    value: IProposalMarineHullDetails[keyof IProposalMarineHullDetails],
    touched = false
  ) => {
    const newPageState = cloneDeep(pageState);
    newPageState.values.marineHullDetails[fieldName] = value;
    newPageState.errors.marineHullDetails[fieldName] =
      validateMarineHullDetailsField(fieldName, value);

    if (touched) {
      newPageState.touched.marineHullDetails[fieldName] = true;
    }

    onPageStateUpdate(newPageState);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Marine Hull Details" hasTitleSpecificDesign={false}>
        <WidgetSection isFieldsGrid>
          <TextInputFormField
            key="vesselName"
            name="vesselName"
            title="Vessel Name*"
            placeholder="Vessel Name"
            value={values.vesselName}
            error={touched.vesselName ? errors.vesselName : ''}
            onBlur={() => onFieldBlur('vesselName')}
            onChange={(v) => onFieldUpdate('vesselName', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="connectionPort"
            name="connectionPort"
            title="Connection Port*"
            placeholder="Connection Port"
            value={values.connectionPort}
            error={touched.connectionPort ? errors.connectionPort : ''}
            onBlur={() => onFieldBlur('connectionPort')}
            onChange={(v) => onFieldUpdate('connectionPort', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="territorialWaters"
            name="territorialWaters"
            title="Territorial Waters*"
            placeholder="Territorial Waters"
            value={values.territorialWaters}
            error={touched.territorialWaters ? errors.territorialWaters : ''}
            onBlur={() => onFieldBlur('territorialWaters')}
            onChange={(v) => onFieldUpdate('territorialWaters', v.target.value)}
            disabled={disabledForm}
            material
          />
        </WidgetSection>
        <WidgetSection title="Body Details" isFieldsGrid>
          <TextInputFormField
            key="usage"
            name="usage"
            title="Usage*"
            placeholder="Usage"
            value={values.usage}
            error={touched.usage ? errors.usage : ''}
            onBlur={() => onFieldBlur('usage')}
            onChange={(v) => onFieldUpdate('usage', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="builder"
            name="builder"
            title="Builder*"
            placeholder="Builder"
            value={values.builder}
            error={touched.builder ? errors.builder : ''}
            onBlur={() => onFieldBlur('builder')}
            onChange={(v) => onFieldUpdate('builder', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="constructionYear"
            name="constructionYear"
            title="Year of Construction*"
            placeholder="Year of Construction"
            value={values.constructionYear}
            error={touched.constructionYear ? errors.constructionYear : ''}
            onBlur={() => onFieldBlur('constructionYear')}
            onChange={(v) => onFieldUpdate('constructionYear', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="registeryPort"
            name="registeryPort"
            title="Port of Registery & Number*"
            placeholder="Port of Registery & Number"
            value={values.registeryPort}
            error={touched.registeryPort ? errors.registeryPort : ''}
            onBlur={() => onFieldBlur('registeryPort')}
            onChange={(v) => onFieldUpdate('registeryPort', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="length"
            name="length"
            title="Length"
            placeholder="Length"
            value={values.length}
            error={touched.length ? errors.length : ''}
            onBlur={() => onFieldBlur('length')}
            onChange={(v) => onFieldUpdate('length', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="weight"
            name="weight"
            title="Weight"
            placeholder="Weight"
            value={values.weight}
            error={touched.weight ? errors.weight : ''}
            onBlur={() => onFieldBlur('weight')}
            onChange={(v) => onFieldUpdate('weight', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="height"
            name="height"
            title="Height"
            placeholder="Height"
            value={values.height}
            error={touched.height ? errors.height : ''}
            onBlur={() => onFieldBlur('height')}
            onChange={(v) => onFieldUpdate('height', v.target.value)}
            disabled={disabledForm}
            material
          />
        </WidgetSection>
        <WidgetSection title="Engine Details" isFieldsGrid>
          <TextInputFormField
            key="engineType"
            name="engineType"
            title="Type*"
            placeholder="Type"
            value={values.engineType}
            error={touched.engineType ? errors.engineType : ''}
            onBlur={() => onFieldBlur('engineType')}
            onChange={(v) => onFieldUpdate('engineType', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="engineNumber"
            name="engineNumber"
            title="Engine Number*"
            placeholder="Engine Number"
            value={values.engineNumber}
            error={touched.engineNumber ? errors.engineNumber : ''}
            onBlur={() => onFieldBlur('engineNumber')}
            onChange={(v) => onFieldUpdate('engineNumber', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="engineYearBuilt"
            name="engineYearBuilt"
            title="Year Built*"
            placeholder="Year Built"
            value={values.engineYearBuilt}
            error={touched.engineYearBuilt ? errors.engineYearBuilt : ''}
            onBlur={() => onFieldBlur('engineYearBuilt')}
            onChange={(v) => onFieldUpdate('engineYearBuilt', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="engineHorsePower"
            name="engineHorsePower"
            title="Horse Power*"
            placeholder="Horse Power"
            value={values.engineHorsePower}
            error={touched.engineHorsePower ? errors.engineHorsePower : ''}
            onBlur={() => onFieldBlur('engineHorsePower')}
            onChange={(v) => onFieldUpdate('engineHorsePower', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="engineMaxDesignedSpeed"
            name="engineMaxDesignedSpeed"
            title="Max. Designed Speed"
            placeholder="Max. Designed Speed"
            value={values.engineMaxDesignedSpeed}
            error={
              touched.engineMaxDesignedSpeed
                ? errors.engineMaxDesignedSpeed
                : ''
            }
            onBlur={() => onFieldBlur('engineMaxDesignedSpeed')}
            onChange={(v) =>
              onFieldUpdate('engineMaxDesignedSpeed', v.target.value)
            }
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="engineFuel"
            name="engineFuel"
            title="Fuel"
            placeholder="Fuel"
            value={values.engineFuel}
            error={touched.engineFuel ? errors.engineFuel : ''}
            onBlur={() => onFieldBlur('engineFuel')}
            onChange={(v) => onFieldUpdate('engineFuel', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="remarks"
            name="remarks"
            title="Remarks"
            placeholder="Remarks"
            value={values.remarks}
            error={touched.remarks ? errors.remarks : ''}
            onBlur={() => onFieldBlur('remarks')}
            onChange={(v) => onFieldUpdate('remarks', v.target.value)}
            disabled={disabledForm}
            material
            fullWidth
          />
        </WidgetSection>
      </WidgetSection>
    </WidgetPaper>
  );
};

export default ProposalMarineHullDetailsWidget;
